import type { AdminServices } from '../adminContracts';
import type { FeatureFlags, OrderStatus, Page } from '@/types';
import type {
  Actor, AdminBanner, AdminCampaign, AdminCategory, AdminCoupon, AdminCustomer, AdminNotification, AdminOffer,
  AdminOrder, AdminProduct, AnalyticsReport, BannerInput, BrandingSettings, CampaignInput, CategoryInput,
  Commission, CommissionAction, CouponInput, CouponRedemption, CustomerProfile, CustomerQuery, DashboardData,
  InventoryEntry, InventoryQuery, InventoryRow, ListQuery, LoyaltyAdjustmentInput, LoyaltyConfig, LoyaltyEntry,
  LoyaltyMemberRow, LoyaltyOverview, LoyaltyQuery, LoyaltyReward, LoyaltyRewardInput, NavigationSettings,
  OfferInput, OrderAdminQuery, OrderNote, OrderTransitionInput, ProductAdminQuery, ProductBulkAction, ProductInput,
  RangeKey, ReferralLink, ReferralOverview, ReferralSettings, ReferredCustomer, SearchHit, StockAdjustmentInput,
  StoreSettings, SystemSettings, ThemeSettings, WalletAdjustmentInput, WalletBalanceRow, WalletEntry, WalletOverview,
  WalletQuery,
} from '@/types/admin';
import { api, qs } from './client';

const A = '/api/admin';

export const httpAdminServices: AdminServices = {
  session: {
    me: () => api<Actor>(`${A}/me`),
  },
  dashboard: {
    summary: (range: RangeKey) => api<DashboardData>(`${A}/dashboard${qs({ range })}`),
    search: (q: string) => api<SearchHit[]>(`${A}/search${qs({ q })}`),
    notifications: () => api<AdminNotification[]>(`${A}/notifications`),
  },
  products: {
    list: (q: ProductAdminQuery) => api<Page<AdminProduct>>(`${A}/products${qs({ ...q })}`),
    get: (id) => api<AdminProduct>(`${A}/products/${id}`),
    create: (input: ProductInput) => api<AdminProduct>(`${A}/products`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminProduct>(`${A}/products/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    remove: (id) => api<void>(`${A}/products/${id}`, { method: 'DELETE' }),
    bulk: (ids, action: ProductBulkAction) => api<{ affected: number }>(`${A}/products/bulk`, { method: 'POST', body: JSON.stringify({ ids, action }) }),
  },
  categories: {
    list: (q) => api<AdminCategory[]>(`${A}/categories${qs({ ...q })}`),
    create: (input: CategoryInput) => api<AdminCategory>(`${A}/categories`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminCategory>(`${A}/categories/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    remove: (id) => api<void>(`${A}/categories/${id}`, { method: 'DELETE' }),
  },
  inventory: {
    list: (q: InventoryQuery) => api<Page<InventoryRow>>(`${A}/inventory${qs({ ...q })}`),
    adjust: (input: StockAdjustmentInput) => api<InventoryEntry>(`${A}/inventory/adjust`, { method: 'POST', body: JSON.stringify(input) }),
    history: (q) => api<Page<InventoryEntry>>(`${A}/inventory/history${qs({ ...q })}`),
  },
  orders: {
    list: (q: OrderAdminQuery) => api<Page<AdminOrder>>(`${A}/orders${qs({ ...q })}`),
    get: (id) => api<AdminOrder>(`${A}/orders/${id}`),
    transition: (id, input: OrderTransitionInput) => api<AdminOrder>(`${A}/orders/${id}/transition`, { method: 'POST', body: JSON.stringify(input) }),
    addNote: (id, text) => api<OrderNote>(`${A}/orders/${id}/notes`, { method: 'POST', body: JSON.stringify({ text }) }),
    counts: () => api<Record<OrderStatus | 'all', number>>(`${A}/orders/counts`),
  },
  customers: {
    list: (q: CustomerQuery) => api<Page<AdminCustomer>>(`${A}/customers${qs({ ...q })}`),
    get: (id) => api<CustomerProfile>(`${A}/customers/${id}`),
    setStatus: (id, status) => api<AdminCustomer>(`${A}/customers/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  },
  coupons: {
    list: (q) => api<Page<AdminCoupon>>(`${A}/coupons${qs({ ...q })}`),
    create: (input: CouponInput) => api<AdminCoupon>(`${A}/coupons`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminCoupon>(`${A}/coupons/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    setEnabled: (id, enabled) => api<AdminCoupon>(`${A}/coupons/${id}/enabled`, { method: 'POST', body: JSON.stringify({ enabled }) }),
    remove: (id) => api<void>(`${A}/coupons/${id}`, { method: 'DELETE' }),
    usage: (id) => api<CouponRedemption[]>(`${A}/coupons/${id}/usage`),
  },
  banners: {
    list: () => api<AdminBanner[]>(`${A}/banners`),
    create: (input: BannerInput) => api<AdminBanner>(`${A}/banners`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminBanner>(`${A}/banners/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    setEnabled: (id, enabled) => api<AdminBanner>(`${A}/banners/${id}/enabled`, { method: 'POST', body: JSON.stringify({ enabled }) }),
    remove: (id) => api<void>(`${A}/banners/${id}`, { method: 'DELETE' }),
  },
  offers: {
    list: () => api<AdminOffer[]>(`${A}/offers`),
    create: (input: OfferInput) => api<AdminOffer>(`${A}/offers`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminOffer>(`${A}/offers/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    setEnabled: (id, enabled) => api<AdminOffer>(`${A}/offers/${id}/enabled`, { method: 'POST', body: JSON.stringify({ enabled }) }),
    remove: (id) => api<void>(`${A}/offers/${id}`, { method: 'DELETE' }),
  },
  campaigns: {
    list: (q) => api<AdminCampaign[]>(`${A}/campaigns${qs({ ...q })}`),
    create: (input: CampaignInput) => api<AdminCampaign>(`${A}/campaigns`, { method: 'POST', body: JSON.stringify(input) }),
    update: (id, input) => api<AdminCampaign>(`${A}/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    send: (id) => api<AdminCampaign>(`${A}/campaigns/${id}/send`, { method: 'POST' }),
    cancel: (id) => api<AdminCampaign>(`${A}/campaigns/${id}/cancel`, { method: 'POST' }),
    remove: (id) => api<void>(`${A}/campaigns/${id}`, { method: 'DELETE' }),
    audienceSize: (audience) => api<{ size: number }>(`${A}/campaigns/audience-size`, { method: 'POST', body: JSON.stringify({ audience }) }).then((r) => r.size),
  },
  wallet: {
    overview: (range: RangeKey) => api<WalletOverview>(`${A}/wallet/overview${qs({ range })}`),
    balances: (q: ListQuery) => api<Page<WalletBalanceRow>>(`${A}/wallet/balances${qs({ ...q })}`),
    transactions: (q: WalletQuery) => api<Page<WalletEntry>>(`${A}/wallet/transactions${qs({ ...q })}`),
    adjust: (input: WalletAdjustmentInput) => api<WalletEntry>(`${A}/wallet/adjust`, { method: 'POST', body: JSON.stringify(input) }),
  },
  loyalty: {
    overview: (range: RangeKey) => api<LoyaltyOverview>(`${A}/loyalty/overview${qs({ range })}`),
    getConfig: () => api<LoyaltyConfig>(`${A}/loyalty/config`),
    saveConfig: (cfg) => api<LoyaltyConfig>(`${A}/loyalty/config`, { method: 'PUT', body: JSON.stringify(cfg) }),
    members: (q) => api<Page<LoyaltyMemberRow>>(`${A}/loyalty/members${qs({ ...q })}`),
    transactions: (q: LoyaltyQuery) => api<Page<LoyaltyEntry>>(`${A}/loyalty/transactions${qs({ ...q })}`),
    adjust: (input: LoyaltyAdjustmentInput) => api<LoyaltyEntry>(`${A}/loyalty/adjust`, { method: 'POST', body: JSON.stringify(input) }),
    listRewards: () => api<LoyaltyReward[]>(`${A}/loyalty/rewards`),
    saveReward: (id, input: LoyaltyRewardInput) => api<LoyaltyReward>(`${A}/loyalty/rewards`, { method: 'PUT', body: JSON.stringify({ id, input }) }),
    removeReward: (id) => api<void>(`${A}/loyalty/rewards/${id}`, { method: 'DELETE' }),
  },
  referrals: {
    overview: () => api<ReferralOverview>(`${A}/referrals/overview`),
    getSettings: () => api<ReferralSettings>(`${A}/referrals/settings`),
    saveSettings: (s) => api<ReferralSettings>(`${A}/referrals/settings`, { method: 'PUT', body: JSON.stringify(s) }),
    links: (q) => api<Page<ReferralLink>>(`${A}/referrals/links${qs({ ...q })}`),
    setLinkActive: (id, active) => api<ReferralLink>(`${A}/referrals/links/${id}/active`, { method: 'POST', body: JSON.stringify({ active }) }),
    referred: (q) => api<Page<ReferredCustomer>>(`${A}/referrals/referred${qs({ ...q })}`),
    commissions: (q) => api<Page<Commission>>(`${A}/referrals/commissions${qs({ ...q })}`),
    actOnCommission: (id, action: CommissionAction) => api<Commission>(`${A}/referrals/commissions/${id}`, { method: 'POST', body: JSON.stringify({ action }) }),
  },
  store: {
    getSettings: () => api<StoreSettings>(`${A}/store/settings`),
    saveSettings: (s) => api<StoreSettings>(`${A}/store/settings`, { method: 'PUT', body: JSON.stringify(s) }),
    getBranding: () => api<BrandingSettings>(`${A}/store/branding`),
    saveBranding: (b) => api<BrandingSettings>(`${A}/store/branding`, { method: 'PUT', body: JSON.stringify(b) }),
    getTheme: () => api<ThemeSettings>(`${A}/store/theme`),
    saveTheme: (t) => api<ThemeSettings>(`${A}/store/theme`, { method: 'PUT', body: JSON.stringify(t) }),
    getNavigation: () => api<NavigationSettings>(`${A}/store/navigation`),
    saveNavigation: (n) => api<NavigationSettings>(`${A}/store/navigation`, { method: 'PUT', body: JSON.stringify(n) }),
  },
  system: {
    get: () => api<SystemSettings & { modules?: FeatureFlags }>(`${A}/system`).then(async (sys) => {
      // modules come from tenant features via /me if missing
      if (!(sys as SystemSettings).modules) {
        try {
          const me = await api<Actor>(`${A}/me`);
          return { ...sys, modules: me.tenant.features } as SystemSettings;
        } catch { /* fall through */ }
      }
      return sys as SystemSettings;
    }),
    saveNotifications: (n, orderPrefix) => api<SystemSettings>(`${A}/system/notifications`, { method: 'PUT', body: JSON.stringify({ notifications: n, orderPrefix }) }),
  },
  analytics: {
    report: (range: RangeKey) => api<AnalyticsReport>(`${A}/analytics${qs({ range })}`),
  },
};
